import { Injectable } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { Subject, filter } from "rxjs";
import { environment } from "../../../environments/environment";
import { PartnerService } from "./partner.service";

export type ConsentStatus = "accepted" | "declined";

interface StoredConsent {
  status: ConsentStatus;
  timestamp: number;
  version: number;
}

// Same key, shape and expiry as mainsite (PSSR-3354)
const STORAGE_KEY = "ps_cookie_consent";
// Bump to ask everyone again after the Cookie Policy changes
const CONSENT_VERSION = 1;
const CONSENT_TTL_MS = 365 * 24 * 60 * 60 * 1000;
const CLARITY_COOKIES = ["_clck", "_clsk"];
// The D2C plans pages: quotation/:id and quotation/renew/:id. Mainsite's D2C quote
// redirect lands on quotation/:id; partners go to comparison/:id instead.
const PLANS_PAGE = /^\/quotation\/(renew\/)?[^/?#]+/;

/**
 * MTR-1017: Microsoft Clarity only runs after the user accepts, and the toast, Cookie
 * Settings link and Clarity only start on the plans pages of PolicyStreet's own D2C
 * hosts. Partner subdomains, ?partnerCode traffic, "/" and comparison/:id get nothing.
 * Once started on a plans page, Clarity keeps recording for the rest of that visit.
 */
@Injectable({
  providedIn: "root"
})
export class CookieConsentService {
  private clarityStarted = false;
  private isD2CVisit = false;
  private ready = false;
  private currentUrl = "";

  /** True only on a D2C plans page — drives the toast, footer link and modal. */
  enabled = false;
  status: ConsentStatus | null = null;
  showToast = false;
  showSettings = false;
  /** Lets the modal reset its draft toggle to the saved choice each time it opens. */
  readonly settingsOpened = new Subject<void>();

  constructor(private partnerService: PartnerService, private router: Router) {}

  /** Called once from AppComponent. */
  async init() {
    // Must run before the first await, so the params are gone before the router reads the URL
    const handoff = this.takeHandoff();

    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.currentUrl = event.urlAfterRedirects;
        this.applyRoute();
      });

    this.isD2CVisit = await this.isD2C();
    if (this.isD2CVisit) {
      let saved = this.read();
      // The choice made on mainsite wins unless the user chose again here afterwards
      if (handoff && (!saved || handoff.timestamp > saved.timestamp)) {
        this.write(handoff);
        saved = handoff;
      }
      this.status = saved?.status ?? null;
    }

    this.ready = true;
    this.applyRoute();
  }

  /** Toast buttons and the settings Save button all end up here. */
  save(status: ConsentStatus) {
    if (!this.enabled) return;

    this.write({ status, timestamp: Date.now(), version: CONSENT_VERSION });
    this.status = status;
    // A choice has been made, so the toast goes even when saved from the settings modal
    this.showToast = false;

    if (status === "accepted") this.startClarity();
    else this.stopClarity();
  }

  openSettings() {
    if (!this.enabled) return;

    this.settingsOpened.next();
    this.showSettings = true;
  }

  closeSettings() {
    this.showSettings = false;
  }

  /** Shows or hides the consent UI for the current page, and starts Clarity if allowed. */
  private applyRoute() {
    if (!this.ready) return;

    this.enabled = this.isD2CVisit && PLANS_PAGE.test(this.currentUrl);
    if (!this.enabled) {
      this.showToast = false;
      this.showSettings = false;
      return;
    }

    this.showToast = this.status === null;
    if (this.status === "accepted") this.startClarity();
  }

  private async isD2C(): Promise<boolean> {
    const partnerCode = await this.partnerService.getPartnerCode();
    const hostName = window.location.hostname.toLowerCase();

    return (
      !partnerCode &&
      this.partnerService.psHostName.some((x) => x.toLowerCase() === hostName)
    );
  }

  /**
   * Reads the choice mainsite passed in the redirect URL (ps_consent / ps_consent_ts),
   * then removes both params so a shared or bookmarked link never carries someone
   * else's consent.
   */
  private takeHandoff(): StoredConsent | null {
    const url = new URL(window.location.href);
    const status = url.searchParams.get("ps_consent");
    const timestamp = Number(url.searchParams.get("ps_consent_ts"));
    if (status === null) return null;

    url.searchParams.delete("ps_consent");
    url.searchParams.delete("ps_consent_ts");
    history.replaceState(history.state, "", url.pathname + url.search + url.hash);

    const handoff = { status, timestamp, version: CONSENT_VERSION } as StoredConsent;
    // Same device clock on both sites, so a choice "from the future" is not genuine
    return this.isValid(handoff) && timestamp <= Date.now() ? handoff : null;
  }

  private isValid(consent: StoredConsent | null): boolean {
    return (
      (consent?.status === "accepted" || consent?.status === "declined") &&
      consent.version === CONSENT_VERSION &&
      Number.isFinite(consent.timestamp) &&
      Date.now() - consent.timestamp < CONSENT_TTL_MS
    );
  }

  private read(): StoredConsent | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;

      const saved = JSON.parse(raw) as StoredConsent;
      const valid = this.isValid(saved);

      if (!valid) localStorage.removeItem(STORAGE_KEY);
      return valid ? saved : null;
    } catch {
      // Blocked storage or corrupt JSON counts as no choice, never as accepted
      return null;
    }
  }

  private write(value: StoredConsent) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch {
      // Storage blocked: the choice still applies for this page load
    }
  }

  private async startClarity() {
    const projectId = environment.clarityProjectId;
    if (this.clarityStarted || !projectId) return;
    this.clarityStarted = true;

    // Loaded on demand so nothing from Clarity reaches the page before consent
    const { default: Clarity } = await import("@microsoft/clarity");
    Clarity.init(projectId);
    Clarity.consent();
  }

  private async stopClarity() {
    if (this.clarityStarted) {
      const { default: Clarity } = await import("@microsoft/clarity");
      Clarity.consent(false);
    }

    // Also clears cookies left from an earlier visit where the user had accepted
    this.deleteCookies(CLARITY_COOKIES);
  }

  private deleteCookies(names: string[]) {
    const parts = window.location.hostname.split(".");
    // Clarity writes to the top-most domain it can, so clear every level
    const domains = parts
      .map((_, i) => parts.slice(i).join("."))
      .filter((domain) => domain.includes("."));

    for (const name of names) {
      document.cookie = `${name}=; Max-Age=0; path=/`;
      for (const domain of domains) {
        document.cookie = `${name}=; Max-Age=0; path=/; domain=.${domain}`;
      }
    }
  }
}
